import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Github, ArrowRight, ExternalLink, Search, Code, TestTube, Star, Users, Linkedin, Quote, type LucideIcon } from "lucide-react";
import { useLanguageNavigation } from "@/components/LanguageRouter";
import ostapPhoto from "@/assets/ostap-dribniuk.png";

interface ToolConfig {
  nameKey: string;
  descriptionKey: string;
  url: string;
  internalUrl?: string;
  icon: LucideIcon;
}

const toolsConfig: ToolConfig[] = [
  {
    nameKey: "openSourceTools.webSearch.title",
    descriptionKey: "openSourceTools.webSearch.description",
    url: "https://github.com/Shelpuk-AI-Technology-Consulting/kindly-web-search-mcp-server",
    internalUrl: "/open-source/kindly-web-search",
    icon: Search,
  },
  {
    nameKey: "openSourceTools.codeReview.title",
    descriptionKey: "openSourceTools.codeReview.description",
    url: "https://github.com/Shelpuk-AI-Technology-Consulting/lad_mcp_server",
    internalUrl: "/open-source/lad-code-review",
    icon: Code,
  },
  {
    nameKey: "openSourceTools.tdd.title",
    descriptionKey: "openSourceTools.tdd.description",
    url: "https://github.com/Shelpuk-AI-Technology-Consulting/agent-skill-tdd",
    internalUrl: "/open-source/tdd",
    icon: TestTube,
  },
];

export function OpenSourceSection() {
  const { t } = useTranslation();
  const { getLocalizedPath } = useLanguageNavigation();

  return (
    <section className="py-20 md:py-24 bg-background">
      <div className="container">
        <div className="max-w-4xl mx-auto">
          {/* Header */}
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary/10 border border-primary/20 text-sm font-medium text-primary mb-6">
              <Github className="h-4 w-4" />
              Open Source
            </div>
            <h2 className="text-3xl font-bold tracking-tight text-foreground sm:text-4xl">
              {t("openSource.title")}
            </h2>
            <p className="mt-2 text-lg text-muted-foreground font-medium">
              {t("openSource.subtitle")}
            </p>
            <p className="mt-4 text-muted-foreground max-w-2xl mx-auto">
              {t("openSource.description")}
            </p>
          </div>

          {/* Stats bar */}
          <div className="flex flex-wrap items-center justify-center gap-8 mb-10 text-sm">
            <div className="flex items-center gap-2 text-muted-foreground">
              <Star className="h-4 w-4 text-primary" />
              <span className="font-semibold text-foreground">350+</span> GitHub Stars
            </div>
            <span className="h-4 w-px bg-border hidden sm:block" />
            <div className="flex items-center gap-2 text-muted-foreground">
              <Users className="h-4 w-4 text-primary" />
              <span className="font-semibold text-foreground">3,000+</span> developers
            </div>
            <span className="h-4 w-px bg-border hidden sm:block" />
            <div className="flex items-center gap-2 text-muted-foreground">
              <Github className="h-4 w-4 text-primary" />
              <span className="font-semibold text-foreground">3</span> products open-sourced
            </div>
          </div>

          {/* Tool cards */}
          <div className="grid gap-4 md:grid-cols-3">
            {toolsConfig.map((tool) => (
              <a
                key={tool.nameKey}
                href={tool.internalUrl ? getLocalizedPath(tool.internalUrl) : tool.url}
                target={tool.internalUrl ? undefined : "_blank"}
                rel={tool.internalUrl ? undefined : "noopener noreferrer"}
                className="group flex flex-col gap-4 p-6 rounded-2xl bg-card border border-border/50 transition-all duration-300 hover:border-primary/40 hover:shadow-lg hover:-translate-y-0.5"
              >
                <div className="flex items-center gap-3">
                  <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-muted text-muted-foreground group-hover:bg-primary/10 group-hover:text-primary transition-colors">
                    <tool.icon className="h-5 w-5" />
                  </div>
                  <h3 className="font-semibold text-foreground group-hover:text-primary transition-colors">
                    {t(tool.nameKey)}
                  </h3>
                </div>
                <p className="text-sm text-muted-foreground flex-1">{t(tool.descriptionKey)}</p>
                <div className="flex items-center justify-end">
                  <div className="flex items-center gap-2 text-sm text-muted-foreground/90 group-hover:text-primary transition-colors">
                    {tool.internalUrl ? (
                      <>
                        <span>{t("common.learnMore")}</span>
                        <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
                      </>
                    ) : (
                      <>
                        <Github className="h-4 w-4" />
                        <span>{t("common.viewOnGitHub")}</span>
                        <ExternalLink className="h-3 w-3" />
                      </>
                    )}
                  </div>
                </div>
              </a>
            ))}
          </div>

          {/* Testimonial */}
          <div className="mt-10 rounded-2xl border border-primary/20 bg-primary/5 p-8 md:p-10">
            <Quote className="h-8 w-8 text-primary/25 mb-4" />
            <p className="text-lg md:text-xl text-foreground leading-relaxed italic mb-6">
              "{t("suiteTestimonial.quote")}"
            </p>
            <div className="flex items-center gap-4">
              <img
                src={ostapPhoto}
                alt={t("suiteTestimonial.name")}
                width={128}
                height={128}
                loading="lazy"
                decoding="async"
                className="h-12 w-12 rounded-full object-cover"
              />
              <div>
                <span className="block font-semibold text-foreground">{t("suiteTestimonial.name")}</span>
                <span className="text-sm text-muted-foreground">{t("suiteTestimonial.role")}</span>
              </div>
              <a
                href="https://www.linkedin.com/in/ostap-dribniuk/"
                target="_blank"
                rel="noopener noreferrer"
                className="ml-auto shrink-0 text-muted-foreground hover:text-primary transition-colors"
                aria-label="Ostap Dribniuk on LinkedIn"
              >
                <Linkedin className="h-5 w-5" />
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
